import React from 'react';
import { ExternalLink } from 'lucide-react';

/**
 * Reminder text has historically embedded URLs (proposal preview links etc.)
 * as raw strings inside the description. Hot-link those so the office team
 * can actually click through. Recognised patterns:
 *   /proposals/{id}/preview
 *   /proposals/{id}
 *   /jobs/{id}
 *   /opportunities/{id}
 *   /contacts/{id}
 *   absolute http(s) URLs
 *
 * Returns an array of React nodes (text and <a> tags), safe to render inside
 * any block element.
 */
const URL_REGEX = /(https?:\/\/[^\s]+|\/(?:proposals|jobs|opportunities|contacts)\/[a-zA-Z0-9\-_]+(?:\/[a-zA-Z0-9\-_]+)?)/g;

export function linkifyReminderText(text) {
  if (!text || typeof text !== 'string') return text || '';
  const parts = [];
  let lastIndex = 0;
  let match;
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    // Strip trailing punctuation that's almost never part of a real URL.
    const trailing = url.match(/[.,;:!?)]+$/);
    const cleanUrl = trailing ? url.slice(0, -trailing[0].length) : url;
    const isInternal = cleanUrl.startsWith('/');
    parts.push(
      <a
        key={`lnk-${match.index}`}
        href={cleanUrl}
        target={isInternal ? '_self' : '_blank'}
        rel={isInternal ? undefined : 'noopener noreferrer'}
        className="inline-flex items-center gap-1 text-amber-600 hover:text-amber-700 underline underline-offset-2 break-all"
        onClick={(e) => e.stopPropagation()}
        data-testid="reminder-inline-link"
      >
        {cleanUrl}
        <ExternalLink className="w-3 h-3 inline-block flex-shrink-0" aria-hidden="true" />
      </a>
    );
    if (trailing) parts.push(trailing[0]);
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

/**
 * Extracts the FIRST recognised link from a reminder's description.
 * Useful for rendering a single prominent "Open …" button (e.g. in the
 * edit dialog) when the reminder relates to a record elsewhere in the app.
 */
export function extractPrimaryLink(text) {
  if (!text || typeof text !== 'string') return null;
  const match = text.match(URL_REGEX);
  if (!match) return null;
  let url = match[0];
  const trailing = url.match(/[.,;:!?)]+$/);
  if (trailing) url = url.slice(0, -trailing[0].length);
  let label = 'Open link';
  if (/\/proposals\//.test(url)) label = 'Open Proposal';
  else if (/\/jobs\//.test(url)) label = 'Open Job';
  else if (/\/opportunities\//.test(url)) label = 'Open Opportunity';
  else if (/\/contacts\//.test(url)) label = 'Open Contact';
  return { url, label, isInternal: url.startsWith('/') };
}
